import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useScrollReveal from '../../hooks/useScrollReveal';

const PaintingCard = ({ painting, onClick, index = 0 }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });
  const { t, i18n } = useTranslation();

  const isEnglish = i18n.language?.startsWith('en');

  const getStatusBadge = (status) => {
    const badges = {
      available: { class: 'badge-available', text: t('modal.status.available') },
      sold: { class: 'badge-sold', text: t('modal.status.sold') },
      private: { class: 'badge-private', text: t('modal.status.private') },
    };
    return badges[status] || badges.available;
  };

  const status = getStatusBadge(painting.status);
  const displayTitle = isEnglish ? (painting.titleEn || painting.title) : painting.title;

  return (
    <div
      ref={ref}
      className={`gallery-item group masonry-item reveal ${isVisible ? 'visible' : ''}`}
      style={{ transitionDelay: `${(index % 4) * 0.1}s` }}
      onClick={() => onClick(painting)}
    >
      <div className="card-elegant overflow-hidden">
        {/* Image Container */}
        <div className="img-zoom-container relative aspect-auto">
          {/* Loading Skeleton */}
          {!imageLoaded && (
            <div className="absolute inset-0 skeleton" />
          )}

          <img
            src={painting.url}
            alt={displayTitle}
            sizes="(max-width: 639px) 100vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw"
            className={`w-full h-auto object-cover transition-opacity duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
          />

          {/* Hover Overlay */}
          <div className="overlay flex items-end justify-center pb-6">
            <div className="flex items-center space-x-2 text-white text-sm tracking-wide transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
              <Eye size={18} />
              <span>{t('featured.details')}</span>
            </div>
          </div>

          {/* Status Badge on Image */}
          <div className="absolute top-3 left-3">
            <span className={`${status.class} text-[10px]`}>
              {status.text}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-5">
          <h3 className="text-lg mb-1 text-[#3D3831] group-hover:text-[#B8963E] transition-colors duration-300">
            {displayTitle}
          </h3>

          <p className="text-sm text-[#8B7E6A] mb-3">
            {painting.year} · {painting.technique}
          </p>

          {painting.price && painting.status === 'available' && (
            <p className="text-[#B8963E] font-medium">
              {painting.price} ₽
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaintingCard;
